import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import { DesignSystemProvider, useDesignSystem } from './contexts/DesignSystemContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { Layout } from './components/layout/Layout';
import { LandingLayout } from './components/layout/LandingLayout';
import { EmailVerificationModal } from './components/modals/EmailVerificationModal';
// The landing page stays eager: it IS the critical path.
import { LandingPage } from './pages/LandingPage';
import { RequireRecruiter } from './components/auth/RequireRecruiter';
import { authService } from './services/auth';
import { identifyUser } from './lib/posthog';
import { usePageTracking } from './hooks/usePageTracking';

/* Route-level code splitting: every non-landing page loads as its own chunk,
   keeping recharts/framer-motion/Stripe and the app surface off the landing
   page's critical path. */
const lazyNamed = <T extends Record<string, any>, K extends keyof T>(
  loader: () => Promise<T>,
  name: K,
) => React.lazy(() => loader().then((m) => ({ default: m[name] })));

const HomePage = lazyNamed(() => import('./pages/HomePage'), 'HomePage');
const PortfolioPage = lazyNamed(() => import('./pages/PortfolioPage'), 'PortfolioPage');
const GoalsAnalysisPage = lazyNamed(() => import('./pages/GoalsAnalysisPage'), 'GoalsAnalysisPage');
const GoalsResumePage = lazyNamed(() => import('./pages/GoalsResumePage'), 'GoalsResumePage');
const ChatPage = lazyNamed(() => import('./pages/ChatPage'), 'ChatPage');
const RefuteGapFlow = lazyNamed(() => import('./pages/RefuteGapFlow'), 'RefuteGapFlow');
const ArtifactViewPage = lazyNamed(() => import('./pages/ArtifactViewPage'), 'ArtifactViewPage');
const VeteransLandingPage = lazyNamed(() => import('./pages/VeteransLandingPage'), 'VeteransLandingPage');
const HowItWorksPage = lazyNamed(() => import('./pages/HowItWorksPage'), 'HowItWorksPage');
const AboutPage = lazyNamed(() => import('./pages/AboutPage'), 'AboutPage');
const ContactPage = lazyNamed(() => import('./pages/ContactPage'), 'ContactPage');
const ResearchPage = lazyNamed(() => import('./pages/ResearchPage'), 'ResearchPage');
const ResearchIndexPage = lazyNamed(() => import('./pages/ResearchIndexPage'), 'ResearchIndexPage');
const TermsPage = lazyNamed(() => import('./pages/TermsPage'), 'TermsPage');
const PrivacyPage = lazyNamed(() => import('./pages/PrivacyPage'), 'PrivacyPage');
const PublicResumeView = lazyNamed(() => import('./pages/PublicResumeView'), 'PublicResumeView');
const AnalyticsPage = lazyNamed(() => import('./pages/AnalyticsPage'), 'AnalyticsPage');
const InviteCompletionPage = lazyNamed(() => import('./pages/InviteCompletionPage'), 'InviteCompletionPage');
const EmailVerificationPage = lazyNamed(() => import('./pages/EmailVerificationPage'), 'EmailVerificationPage');
const OAuthCallbackPage = lazyNamed(() => import('./pages/OAuthCallbackPage'), 'OAuthCallbackPage');
const SignupPage = lazyNamed(() => import('./pages/SignupPage'), 'SignupPage');
const ClaimPage = lazyNamed(() => import('./pages/ClaimPage'), 'ClaimPage');
const RecruiterConsentsPage = lazyNamed(() => import('./pages/RecruiterConsentsPage'), 'RecruiterConsentsPage');
const RecruiterDashboardPage = lazyNamed(() => import('./pages/recruiter/RecruiterDashboardPage'), 'RecruiterDashboardPage');
const RosterPage = lazyNamed(() => import('./pages/recruiter/RosterPage'), 'RosterPage');
const SearchesPage = lazyNamed(() => import('./pages/recruiter/SearchesPage'), 'SearchesPage');
const SearchDetailPage = lazyNamed(() => import('./pages/recruiter/SearchDetailPage'), 'SearchDetailPage');
const ImportPage = lazyNamed(() => import('./pages/recruiter/ImportPage'), 'ImportPage');
const AnnouncementsListPage = React.lazy(() => import('./pages/AnnouncementsListPage'));
const AnnouncementPage = React.lazy(() => import('./pages/AnnouncementPage'));
const AnnouncementsManagementPage = React.lazy(() => import('./pages/AnnouncementsManagementPage'));
const DesignSystemPage = lazyNamed(() => import('./pages/DesignSystemPage'), 'DesignSystemPage');
// Onboarding tooltips ship framer-motion; load them off the critical path.
const OnboardingManager = lazyNamed(
  () => import('./components/onboarding').then((m) => m),
  'OnboardingManager',
);

// Hook to get onGetStarted from LandingLayout
export function useLandingContext() {
  return useOutletContext<{ onGetStarted: () => void }>();
}

// Inner component that has access to navigation hooks
function AppRoutes() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  usePageTracking();

  // Development bypass: allow access to app without authentication
  const DEV_BYPASS_AUTH = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';

  // Initial auth check on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authed = authService.isAuthenticated();
        setIsAuthenticated(authed);

        // Fetch user profile if authenticated
        if (authed) {
          try {
            const userProfile = await authService.getCurrentUser();
            setUser(userProfile);

            identifyUser(userProfile.id, {
              email: userProfile.email,
              name: userProfile.name,
              is_admin: userProfile.is_admin,
              is_verified: userProfile.is_verified,
              partner: userProfile.partner ?? null,
              account_type: userProfile.account_type,
              agency_id: userProfile.agency_id ?? null,
              signup_date: userProfile.created_at,
            });

            // Check if user is trying to access app but email not verified
            if (
              !userProfile.is_verified &&
              (location.pathname.startsWith('/app') || location.pathname.startsWith('/recruiter'))
            ) {
              setShowVerificationModal(true);
            }
          } catch (error) {
            console.error('Failed to fetch user profile:', error);
          }
        }
      } catch {
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };
    checkAuth();
  }, [location.pathname]);

  // Reactive auth state detection - listen for token removal
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Detect when access token is removed from localStorage
      if (e.key === 'access_token' && !e.newValue) {
        setIsAuthenticated(false);
      }
      // Detect when access token is added (login in another tab)
      if (e.key === 'access_token' && e.newValue) {
        setIsAuthenticated(true);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Sync auth state when location changes (catch logout in same tab)
  useEffect(() => {
    const hasToken = authService.isAuthenticated();
    if (hasToken !== isAuthenticated) {
      setIsAuthenticated(hasToken);
    }
  }, [location.pathname, isAuthenticated]);

  // Redirect to landing page if auth is lost while on protected route
  useEffect(() => {
    // Wait for initial auth check to complete before redirecting
    if (!isLoading && !isAuthenticated && !DEV_BYPASS_AUTH && location.pathname.startsWith('/app')) {
      navigate('/', { replace: true });
    }
  }, [isLoading, isAuthenticated, location.pathname, navigate, DEV_BYPASS_AUTH]);

  const handleLogin = async () => {
    setIsAuthenticated(true);

    // Recruiters land on their own surface; candidates keep today's flow.
    let destination = '/app';

    // Fetch user profile to populate is_admin and is_verified fields
    try {
      const userProfile = await authService.getCurrentUser();
      setUser(userProfile);

      identifyUser(userProfile.id, {
        email: userProfile.email,
        name: userProfile.name,
        is_admin: userProfile.is_admin,
        is_verified: userProfile.is_verified,
        partner: userProfile.partner ?? null,
        account_type: userProfile.account_type,
        agency_id: userProfile.agency_id ?? null,
        signup_date: userProfile.created_at,
      });

      if (userProfile.account_type === 'recruiter') {
        destination = '/recruiter';
      }

      // Check if email is verified
      if (!userProfile.is_verified) {
        // Show verification modal instead of navigating to app
        setShowVerificationModal(true);
        return;
      }
    } catch (error) {
      console.error('Failed to fetch user profile after login:', error);
    }

    navigate(destination);  // Navigate to the persona home after successful login
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="mx-auto mb-4 size-12 animate-spin rounded-full border-b-2 border-primary"></div>
          <p className="text-foreground/70">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {/* Onboarding Manager - renders tooltips across the app (own chunk) */}
      <React.Suspense fallback={null}>
        <OnboardingManager />
      </React.Suspense>

      {/* Email Verification Modal - shown when unverified user tries to access app */}
      <EmailVerificationModal
        isOpen={showVerificationModal}
        onClose={() => {
          setShowVerificationModal(false);
          // After closing, check if user is now verified
          authService.getCurrentUser().then(userProfile => {
            setUser(userProfile);
            if (userProfile.is_verified) {
              navigate(userProfile.account_type === 'recruiter' ? '/recruiter' : '/app');
            }
          }).catch(() => {});
        }}
      />

      <React.Suspense
        fallback={
          <div className="flex min-h-screen items-center justify-center bg-background">
            <div className="mx-auto size-12 animate-spin rounded-full border-b-2 border-primary"></div>
          </div>
        }
      >
      <Routes>
        {/* Landing Pages (Public - Always accessible) */}
        <Route path="/" element={<LandingLayout onLogin={handleLogin} />}>
          <Route index element={<LandingPage />} />
        </Route>
        <Route path="/veterans" element={<LandingLayout onLogin={handleLogin} partner="vt_veterans" />}>
          <Route index element={<VeteransLandingPage />} />
        </Route>
        <Route path="/how-it-works" element={<LandingLayout onLogin={handleLogin} />}>
          <Route index element={<HowItWorksPage />} />
        </Route>
        <Route path="/about" element={<LandingLayout onLogin={handleLogin} />}>
          <Route index element={<AboutPage />} />
        </Route>
        <Route path="/contact" element={<LandingLayout onLogin={handleLogin} />}>
          <Route index element={<ContactPage />} />
        </Route>
        <Route path="/research" element={<LandingLayout onLogin={handleLogin} />}>
          <Route index element={<ResearchIndexPage />} />
        </Route>
        <Route path="/research/benchmarking-46-llms" element={<LandingLayout onLogin={handleLogin} />}>
          <Route index element={<ResearchPage />} />
        </Route>
        <Route path="/terms" element={<LandingLayout onLogin={handleLogin} />}>
          <Route index element={<TermsPage />} />
        </Route>
        <Route path="/privacy" element={<LandingLayout onLogin={handleLogin} />}>
          <Route index element={<PrivacyPage />} />
        </Route>

        {/* Announcements Pages (Public) */}
        <Route path="/announcements" element={<LandingLayout onLogin={handleLogin} />}>
          <Route index element={<AnnouncementsListPage />} />
        </Route>
        <Route path="/announcements/:slug" element={<LandingLayout onLogin={handleLogin} />}>
          <Route index element={<AnnouncementPage />} />
        </Route>

        {/* Signup (register-mode auth dialog). Destination of recruiter invite
            URLs (?invite=<token>) and the landing recruiter CTA (?type=recruiter). */}
        <Route path="/signup" element={<SignupPage onAuthenticated={handleLogin} />} />

        {/* Public Resume View - Personal Advocate (No auth required) */}
        <Route path="/resume/:accessToken" element={<PublicResumeView />} />

        {/* Recruiter claim flow (public info; claiming itself requires auth in-page) */}
        <Route path="/claim/:token" element={<ClaimPage />} />

        {/* Invite & Verification Routes (Public - No auth required) */}
        <Route path="/invite/:token" element={<InviteCompletionPage />} />
        <Route path="/verify-email/:token" element={<EmailVerificationPage />} />

        {/* OAuth Callback Route (Public - Handles OAuth redirects) */}
        <Route path="/auth/callback" element={<OAuthCallbackPage />} />

        {/* Authenticated App Routes - All under /app prefix */}
        {(isAuthenticated || DEV_BYPASS_AUTH) ? (
          <Route path="/app" element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path="portfolio" element={<PortfolioPage />} />
            <Route path="portfolio/edit" element={<PortfolioPage />} />
            <Route path="portfolio/advocate" element={<PortfolioPage />} />
            <Route path="portfolio/share" element={<PortfolioPage />} />
            {/* Admin Routes */}
            {user?.is_admin && <Route path="analytics" element={<AnalyticsPage />} />}
            {user?.is_admin && <Route path="announcements-admin" element={<AnnouncementsManagementPage />} />}

            {/* Redirects - Goals and Documents moved to HomePage */}
            <Route path="goals" element={<Navigate to="/app" replace />} />
            <Route path="artifacts" element={<Navigate to="/app" replace />} />

            {/* Keep detail pages */}
            <Route path="goals/:goalId/analysis" element={<GoalsAnalysisPage />} />
            <Route path="goals/:goalId/resume" element={<GoalsResumePage />} />
            <Route path="artifacts/view/:artifactId" element={<ArtifactViewPage />} />
            <Route path="chat" element={<Navigate to="/app" replace />} />
            <Route path="chat/:agentId" element={<ChatPage />} />
            <Route path="gaps/:gapId/refute/:goalId?" element={<RefuteGapFlow />} />

            {/* Candidate consent center for recruiter access */}
            <Route path="recruiters" element={<RecruiterConsentsPage />} />
          </Route>
        ) : null}

        {/* Recruiter surfaces - guarded by account type (RequireRecruiter
            redirects unauthenticated visitors to /signup?type=recruiter and
            authenticated candidates to /app) */}
        <Route
          path="/recruiter"
          element={
            <RequireRecruiter isAuthenticated={isAuthenticated || DEV_BYPASS_AUTH} user={user}>
              <Layout />
            </RequireRecruiter>
          }
        >
          <Route index element={<RecruiterDashboardPage />} />
          <Route path="roster" element={<RosterPage />} />
          <Route path="searches" element={<SearchesPage />} />
          <Route path="searches/:searchId" element={<SearchDetailPage />} />
          <Route path="import" element={<ImportPage />} />
        </Route>

        {/* Dev-only design-system exemplar route */}
        {import.meta.env.DEV && (
          <Route
            path="/design"
            element={
              <React.Suspense fallback={null}>
                <DesignSystemPage />
              </React.Suspense>
            }
          />
        )}
        {/* Catch-all - redirect to landing */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </React.Suspense>
    </div>
  );
}

// Toast notifications on the token system; sonner's theme (richColors
// palettes + close-button chrome) follows the active design-system theme.
function ThemedToaster() {
  const { theme } = useDesignSystem();
  return (
    <Toaster
      position="top-right"
      toastOptions={{
        style: {
          background: 'var(--popover)',
          border: '1px solid var(--border)',
          color: 'var(--popover-foreground)',
        },
      }}
      theme={theme}
      richColors
      closeButton
    />
  );
}

// Main App component that wraps everything in Router
function App() {
  return (
    <HelmetProvider>
      <DesignSystemProvider>
        <OnboardingProvider>
          <Router>
            <AppRoutes />
          </Router>
          <ThemedToaster />
        </OnboardingProvider>
      </DesignSystemProvider>
    </HelmetProvider>
  );
}

export default App;
